import { useEffect, useRef } from 'react';

const CHORD_TIMEOUT_MS = 900;

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * Registers a sequence hotkey such as `g o`. Sequences, not modifiers: a production tool is
 * used with both hands on the keyboard, and browser/OS chords are already taken.
 */
export function useHotkey(sequence: string | undefined, handler: () => void): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!sequence) return;
    const keys = sequence.toLowerCase().split(/\s+/).filter(Boolean);
    if (keys.length === 0) return;

    let position = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const reset = (): void => {
      position = 0;
      if (timer) clearTimeout(timer);
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      if (event.key.toLowerCase() !== keys[position]) {
        reset();
        return;
      }
      position += 1;
      if (position === keys.length) {
        reset();
        event.preventDefault();
        handlerRef.current();
        return;
      }
      if (timer) clearTimeout(timer);
      timer = setTimeout(reset, CHORD_TIMEOUT_MS);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      if (timer) clearTimeout(timer);
    };
  }, [sequence]);
}
